import React, { useEffect, useRef } from 'react';
import V3ErrorBoundary from './V3ErrorBoundary';
import { v3CSSVariables } from '../theme';
import { perfMark } from '../../utils/perfTracker';

// List of critical icons to preload
const CRITICAL_ICONS = [
    '/img/world.webp',
    '/img/inventory.webp',
    '/img/profile.webp',
    '/img/guild.webp',
    '/img/feed.webp',
    '/img/userpic.png',
    '/img/health.webp',
    '/img/steps.webp'
];

// Store loaded images to prevent garbage collection
const loadedImages = [];

const V3ResourceLoader = ({ children }) => {
    const perfMarkedRef = useRef(false);

    useEffect(() => {
        // Add CSS variables immediately
        const style = document.createElement('style');
        style.textContent = `:root { ${v3CSSVariables} }`;
        document.head.appendChild(style);

        // Add font link — loads in background, does not block render
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        // Preload critical icons in background
        const iconPromises = CRITICAL_ICONS.map(src => {
            return new Promise((resolve) => {
                const preloadLink = document.createElement('link');
                preloadLink.rel = 'preload';
                preloadLink.as = 'image';
                preloadLink.href = src;
                document.head.appendChild(preloadLink);

                const img = new Image();
                img.onload = () => {
                    loadedImages.push(img);
                    if ('decode' in img) {
                        img.decode().then(() => resolve()).catch(() => resolve());
                    } else {
                        resolve();
                    }
                };
                img.onerror = () => resolve();
                img.src = src;
            });
        });

        // Track when all resources finish loading (non-blocking)
        Promise.all(iconPromises).then(() => {
            if (!perfMarkedRef.current) {
                perfMarkedRef.current = true;
                perfMark('resourcesLoaded');
            }
        });
    }, []);

    // Always render children immediately — never block on fonts/icons
    return (
        <V3ErrorBoundary fallbackMessage="Failed to load application resources">
            {children}
        </V3ErrorBoundary>
    );
};

export default V3ResourceLoader;
